import { AuthRepository } from '@/core/data/auth/auth.repository';
import { JwtRepository } from '@/core/data/auth/jwt.repository';
import { ClientConfigRepository } from '@/core/data/config/clientConfig.repository';
import { UIConfigRepository } from '@/core/data/config/uiConfig.repository';
import { FilterRepository } from '@/core/data/filter/filter.repository';
import { GambitRepository } from '@/core/data/gambit/gambit.repository';
import { ImageRepository } from '@/core/data/image/image.repository';
import { LocalizationRepository } from '@/core/data/localization/localization.repository';
import type { ObjectType } from '@/core/data/localization/localization.type';
import { LocationRepository } from '@/core/data/location/location.repository';
import { RatingRepository } from '@/core/data/rating/rating.repository';
import { ReportRepository } from '@/core/data/report/report.repository';
import { StorageType } from '@/core/data/storage/storage.interface';
import { StorageRepository } from '@/core/data/storage/storage.repository';
import { UserRepository } from '@/core/data/user/user.repository';
import { AuthService } from '@/core/domain/auth/auth.service';
import { JwtService } from '@/core/domain/auth/jwt.service';
import { ConfigService } from '@/core/domain/config.service';
import { FilterService } from '@/core/domain/filter.service';
import { GambitService } from '@/core/domain/gambit/gambit.service';
import { HomeViewService } from '@/core/domain/homeView.service';
import { ImageService } from '@/core/domain/image.service';
import { LocalizationService } from '@/core/domain/localization.service';
import { GeolocationService } from '@/core/domain/location/geolocation.service';
import { LocationService } from '@/core/domain/location/location.service';
import { RatingService } from '@/core/domain/rating/rating.service';
import { ReportService } from '@/core/domain/report.service';
import { StaticService } from '@/core/domain/static/static.service';
import { UserService } from '@/core/domain/user/user.service';
import { AuthApi } from '@/core/network/api/auth.api';
import { FiltersApi } from '@/core/network/api/filters.api';
import { GambitApi } from '@/core/network/api/gambit/gambit.api';
import { ImageApi } from '@/core/network/api/image.api';
import { LocationApi } from '@/core/network/api/location/location.api';
import { RatingApi } from '@/core/network/api/rating/rating.api';
import { ReportApi } from '@/core/network/api/report.api';
import { UserApi } from '@/core/network/api/user/user.api';
import { HttpClient } from '@/core/network/http/httpClient';
import type { Head, UseSeoMetaInput } from '@unhead/vue';
import { ClubRepository } from './data/club/club.repository';
import { AdminService } from './domain/admin.service';
import { ClubService } from './domain/club/club.service';
import { AdminApi } from './network/api/admin.api';
import { ClubApi } from './network/api/club/club.api';

import { ChatRepository } from './data/chat/chat.repository';
import { ChessboardRepository } from './data/chessboard/chessboard.repository';
import { DialogRepository } from './data/dialog/dialog.repository';
import { DuelRepository } from './data/duel/duel.repository';
import { GeolocationRepository } from './data/location/geolocation.repository';
import { NotificationRepository } from './data/notification/notification.repository';
import { SeedRepository } from './data/seed/seed.repository';
import { SparkRepository } from './data/spark/spark.repository';
import { ChatService } from './domain/chat/chat.service';
import { ChessboardService } from './domain/chessboard/chessboard.service';
import { DuelService } from './domain/duel/duel.service';
import { NotificationService } from './domain/notification/notification.service';
import { SeedService as FrontendSeedService } from './domain/seed/seed.service';
import { SparkService } from './domain/sparks/spark.service';
import data from './gambits.json';
import { BugReportApi } from './network/api/bug-report.api';
import { ChatApi } from './network/api/chat/chat.api';
import { ChessboardApi } from './network/api/chessboard/chessboard.api';
import { DuelApi } from './network/api/duel/duel.api';
import { MapPuzzleApi } from './network/api/map-puzzle/map-puzzle.api';
import { NotificationApi } from './network/api/notification/notification.api';
import { SeedApi } from './network/api/seed/seed.api';
import { SparkApi } from './network/api/spark/spark.api';
import { WalkApi } from './network/api/walk/walk.api';

const ogImage = 'https://gambits.com/images/og-gambits-261012a.png';

class Gambits {
  private readonly httpClient: HttpClient;
  public readonly configService: ConfigService;
  public readonly staticService: StaticService;
  public readonly localizationService: LocalizationService;
  public readonly authService: AuthService;
  public readonly adminService: AdminService;

  public readonly homeViewService: HomeViewService;

  public readonly userService: UserService;
  public readonly locationService: LocationService;
  public readonly sparkService: SparkService;
  public readonly gambitService: GambitService;
  public readonly chatService: ChatService;
  public readonly filterService: FilterService;
  public readonly imageService: ImageService;
  public readonly notificationService: NotificationService;
  public readonly ratingService: RatingService;
  public readonly geolocationService: GeolocationService;
  public readonly reportService: ReportService;
  public readonly clubService: ClubService;
  public readonly chessboardService: ChessboardService;
  public readonly duelService: DuelService;
  public readonly seedService: FrontendSeedService;
  public readonly mapPuzzleApi: MapPuzzleApi;
  public readonly walkApi: WalkApi;
  public readonly bugReportApi: BugReportApi;
  public readonly jwtService: JwtService;
  public readonly dialogRepository: DialogRepository;

  constructor() {
    // Local config repository
    const clientConfigRepository = new ClientConfigRepository();
    const spices = clientConfigRepository.parseServiceSpices();

    const localStorage = new StorageRepository(window.localStorage, StorageType.Local, spices);
    const sessionStorage = new StorageRepository(window.sessionStorage, StorageType.Session, spices);

    // Config service
    const uiConfigRepository = new UIConfigRepository(localStorage);
    this.configService = new ConfigService(clientConfigRepository, uiConfigRepository);

    // Create an instance of HttpClient object
    const jwtRepository = new JwtRepository(localStorage, sessionStorage);
    this.jwtService = new JwtService(jwtRepository, this.configService.getServiceRoutingHost());
    this.httpClient = new HttpClient(this.jwtService, this.configService.getServiceRoutingHost());

    // Admin service
    const adminApi = new AdminApi(this.httpClient);
    this.adminService = new AdminService(adminApi);

    // Localization service
    const localizationRepository = new LocalizationRepository();
    this.localizationService = new LocalizationService(localizationRepository, this.configService);

    // User service
    const userApi = new UserApi(this.httpClient);
    const userRepository = new UserRepository(userApi, sessionStorage);
    this.userService = new UserService(userRepository);

    // Auth service
    const authApi = new AuthApi(this.httpClient);
    const authRepository = new AuthRepository(jwtRepository, userRepository, authApi, localStorage, sessionStorage);
    this.authService = new AuthService(authRepository, this.configService);

    this.staticService = new StaticService();

    // HomeView
    const uiConfig = this.configService.getUIConfig();
    this.homeViewService = new HomeViewService(uiConfig);

    // Spots
    const locationApi = new LocationApi(this.httpClient);
    const locationRepository = new LocationRepository(
      locationApi,
      localStorage,
      clientConfigRepository,
      this.localizationService,
    );
    this.locationService = new LocationService(locationRepository);

    // Sparks
    const sparkApi = new SparkApi(this.httpClient);
    const sparksRepository = new SparkRepository(sparkApi, localStorage, clientConfigRepository);
    this.sparkService = new SparkService(sparksRepository);

    // Clubs
    const clubApi = new ClubApi(this.httpClient);
    const clubRepository = new ClubRepository(clubApi);
    this.clubService = new ClubService(clubRepository);

    // Chessboards
    const chessboardApi = new ChessboardApi(this.httpClient);
    const chessboardRepository = new ChessboardRepository(chessboardApi);
    this.chessboardService = new ChessboardService(chessboardRepository);

    // Duels
    const duelApi = new DuelApi(this.httpClient);
    const duelRepository = new DuelRepository(duelApi);
    this.duelService = new DuelService(duelRepository);

    // Walks
    this.mapPuzzleApi = new MapPuzzleApi(this.httpClient);
    this.walkApi = new WalkApi(this.httpClient);

    // Bug Reports
    this.bugReportApi = new BugReportApi(this.httpClient);

    // Seeds
    const seedApi = new SeedApi(this.httpClient);
    const seedRepository = new SeedRepository(seedApi);
    this.seedService = new FrontendSeedService(seedRepository);

    // Gambits
    const gambitApi = new GambitApi(this.httpClient);
    const gambitRepository = new GambitRepository(gambitApi);
    this.gambitService = new GambitService(gambitRepository);

    // Chats
    const chatApi = new ChatApi(this.httpClient);
    const chatRepository = new ChatRepository(chatApi);
    this.chatService = new ChatService(chatRepository);

    // Filters
    const filterApi = new FiltersApi(this.httpClient);
    const filterRepository = new FilterRepository(filterApi, sessionStorage);
    this.filterService = new FilterService(filterRepository);

    // Images
    const imageApi = new ImageApi(this.httpClient);
    const imageRepository = new ImageRepository(imageApi);
    this.imageService = new ImageService(imageRepository, this.configService);

    // Notifications
    const notificationApi = new NotificationApi(this.httpClient);
    const notificationRepository = new NotificationRepository(notificationApi);
    this.notificationService = new NotificationService(notificationRepository);

    // Ratings
    const ratingApi = new RatingApi(this.httpClient);
    const ratingRepository = new RatingRepository(ratingApi, sessionStorage);
    this.ratingService = new RatingService(ratingRepository);

    // Reports
    const reportApi = new ReportApi(this.httpClient);
    const reportRepository = new ReportRepository(reportApi);
    this.reportService = new ReportService(reportRepository);

    // Geolocation
    const geolocationRepository = new GeolocationRepository(locationApi, localStorage, clientConfigRepository);
    this.geolocationService = new GeolocationService(geolocationRepository);

    this.dialogRepository = new DialogRepository(localStorage);
  }

  getPageMeta(): Head {
    return {
      meta: [
        {
          name: 'keywords',
          content: data.keywords,
        },
      ],
    };
  }

  useSeoMeta(): UseSeoMetaInput {
    return {
      title: data.title,
      description: data.description,
      ogType: 'website',
      ogTitle: data.title,
      ogDescription: data.description,
      ogImage: { url: ogImage, type: 'image/png' },
      twitterImage: ogImage,
    };
  }
}

const gambits = new Gambits();

const localize = (translationKey: string): string => {
  const translation = gambits.localizationService.localize(translationKey);

  if (translation === null) return '';
  if (translation === undefined) return translationKey;

  return translation;
};

const localizeWithValues = (translationKey: string, values: Array<string | number | undefined>): string => {
  return gambits.localizationService.localizeWithValues(translationKey, values) || translationKey;
};

const localizeType = (type: ObjectType, translationKey: string): string => {
  return gambits.localizationService.localizeType(type, translationKey) || translationKey || '';
};

export { gambits, localize, localizeType, localizeWithValues };
